/**
 * The clinic directory: every doctor in the shared clinical database, and how
 * to amend them.
 *
 * Creating a center finds or registers its doctor; this page is the rest of
 * the lifecycle. The rows live in the *shared clinical database* used by real
 * clinicians, which shapes every decision here:
 *
 *   - One row per doctor, cut into pages on the server
 *     (`GET /api/clinics?q=&status=&limit=&offset=`). The doctor table belongs
 *     to another application and holds thousands of rows, so the browser never
 *     receives it whole. Search, status, page and page size live in the URL,
 *     so a refresh, back/forward or a pasted link lands on the same page. The
 *     request path is `useResource`'s key; that hook aborts the in-flight
 *     request and discards its late answer whenever the key changes, so an
 *     out-of-order response can never overwrite a newer one.
 *
 *   - Every number on screen — header badge, filter-bar count, KPI tiles, the
 *     pager — comes from the response's `total` and `counts`, never from
 *     `rows.length`, which is one page and would describe nothing.
 *
 *   - The KPI tiles are the filter controls, exactly as on the Issues page:
 *     buttons wearing the metric skin, `aria-pressed` carrying the state, and
 *     pressing an active tile clears back to All. The filter is the server's
 *     `status=`, so a tile's count and the pages it narrows to are one query.
 *
 *   - Status is three states, aggregated over every membership the doctor's
 *     login has: Active (at least one active), Inactive (memberships exist,
 *     none active — retired here *or* an invite nobody accepted; the WebAPI
 *     stores both as 0) and Incomplete (no login or no membership at all).
 *     Retire and restore act on one membership at a time, in the panel,
 *     because a doctor who belongs to five clinics has five separate grants.
 *
 *   - There is no client-side role gating, matching the Operators page: writes
 *     are owner-only on the server, and a 403's detail is surfaced verbatim in
 *     the panel rather than the controls being hidden by a client-side guess.
 *
 *   - "No clinics match" and "no clinics exist" are different sentences. An
 *     empty search result rendered as an empty directory would read as data
 *     loss, and the reverse would send someone hunting for a filter that is
 *     not set.
 *
 *   - Revisions are shown before → after because the clinical schema has no
 *     history of its own: this controller's `clinic_revisions` is the only
 *     record that an email was changed here rather than by the WebAPI.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Badge, Tag, type Tone } from '../components/Badge';
import { Callout } from '../components/Callout';
import { PageHeader } from '../components/PageHeader';
import { StatGrid, type MetricTone } from '../components/StatTile';
import { EmptyBlock, ErrorBlock, SkeletonRows, StaleBanner } from '../components/States';
import { Button } from '../components/ui/Button';
import { Card, KeyValue, KeyValueRow } from '../components/ui/Card';
import { DataTable, type Column } from '../components/ui/DataTable';
import { DetailPanel } from '../components/ui/DetailPanel';
import { FilterBar } from '../components/ui/FilterBar';
import { TextArea, TextInput } from '../components/ui/Form';
import { ChevronRightIcon } from '../components/ui/Icon';
import { DEFAULT_PAGE_SIZES, Pager } from '../components/ui/Pager';
import { apiPost, apiPut, describeError } from '../lib/api';
import { formatDateTime, formatNumber, formatRelative, pluralise, UNKNOWN } from '../lib/format';
import { useResource } from '../lib/useResource';
import type {
  ClinicCounts,
  ClinicDetail,
  ClinicListResponse,
  ClinicMembership,
  ClinicRestoreRequest,
  ClinicRetireRequest,
  ClinicRevisionResponse,
  ClinicStatusFilter,
  ClinicSummary,
  ClinicUpdateRequest,
} from '../types/api';

import '../styles/page-clinics.css';

/* ── URL state ──────────────────────────────────────────────────────────────
   Every value is read defensively: a hand-edited or stale link that the
   server would 422 on reads as the default instead of as an error page. The
   defaults themselves are never written, so the page at rest is `/clinics`. */

const DEFAULT_PAGE_SIZE = 50;

/** Keeps `offset` a plain integer in the query string — `1e21` would 422. A
 *  page past the end is clamped once the answer arrives, so the cap is never
 *  visible. */
const MAX_PAGE = 1_000_000;
/** The server refuses (422) a `q` longer than this; clamp before it is sent. */
const MAX_Q = 200;

const NO_COUNTS: ClinicCounts = { all: 0, orphaned: 0, inactive: 0 };

type UrlKey = 'q' | 'status' | 'page' | 'size';

function readStatus(raw: string | null): ClinicStatusFilter {
  return raw === 'orphaned' || raw === 'inactive' ? raw : 'all';
}

function readPage(raw: string | null): number {
  const page = Number(raw);
  return Number.isInteger(page) && page >= 1 ? Math.min(page, MAX_PAGE) : 1;
}

function readSize(raw: string | null): number {
  const size = Number(raw);
  return DEFAULT_PAGE_SIZES.includes(size) ? size : DEFAULT_PAGE_SIZE;
}

const pageParam = (page: number) => (page <= 1 ? null : page);
const sizeParam = (size: number) => (size === DEFAULT_PAGE_SIZE ? null : size);
const statusParam = (status: ClinicStatusFilter) => (status === 'all' ? null : status);

/* ── status ─────────────────────────────────────────────────────────────── */

function ClinicStatusBadge({ clinic }: { clinic: ClinicSummary }) {
  if (clinic.status === 'inactive') {
    return (
      <Badge
        tone="neutral"
        small
        title="No membership is active: every workspace this login belongs to has active = 0 — retired here, or an invite nobody accepted (the WebAPI stores both as 0). The rows are kept."
      >
        Inactive
      </Badge>
    );
  }
  if (clinic.status === 'incomplete') {
    return (
      <Badge
        tone="warning"
        small
        title="No login user or no workspace membership exists — the account was never finished, so there is nothing to grant or revoke."
      >
        Incomplete
      </Badge>
    );
  }
  return (
    <Badge
      tone="good"
      small
      title={
        clinic.active_membership_count < clinic.membership_count
          ? `${clinic.active_membership_count} of ${clinic.membership_count} memberships active`
          : undefined
      }
    >
      Active
    </Badge>
  );
}

/**
 * A KPI that is also the filter for the rows it counts — the Issues page
 * pattern verbatim: a `<button>` wearing the metric skin, `aria-pressed`
 * carrying the on/off state, and pressing the active tile clearing back to
 * All rather than leaving the operator hunting for a way out.
 */
function FilterTile({
  label,
  value,
  hint,
  tone,
  active,
  onPick,
}: {
  label: string;
  value: string;
  hint: string;
  tone?: MetricTone;
  active: boolean;
  onPick: (active: boolean) => void;
}) {
  return (
    <button
      type="button"
      className={`metric cl-kpi${tone ? ` metric--${tone}` : ''}`}
      aria-pressed={active}
      title={
        active
          ? `Showing ${label.toLowerCase()} only — press to show every clinic`
          : `Show ${label.toLowerCase()} only`
      }
      onClick={() => onPick(active)}
    >
      <span className="metric__head">
        <span className="metric__label">{label}</span>
      </span>
      <span className="metric__value">{value}</span>
      <span className="metric__hint">{hint}</span>
    </button>
  );
}

/** The login email is worth a second line only when it says something the
 *  doctor email does not — the search matches both, and a hit on the login
 *  alone would otherwise look like a false positive. */
function loginDiffers(clinic: ClinicSummary): boolean {
  if (clinic.user_email === null) return false;
  if (clinic.doctor_email === null) return true;
  return clinic.user_email.toLowerCase() !== clinic.doctor_email.toLowerCase();
}

export function ClinicsPage() {
  const [params, setParams] = useSearchParams();
  const q = (params.get('q') ?? '').trim().slice(0, MAX_Q);
  const status = readStatus(params.get('status'));
  const page = readPage(params.get('page'));
  const size = readSize(params.get('size'));
  const offset = (page - 1) * size;

  // One URL write per user action, carrying every key it moves: a new search
  // and its reset to page 1 land together, so there is one navigation and one
  // request, never a request for page 3 of the new term first.
  const navigateTo = useCallback(
    (changes: Partial<Record<UrlKey, string | number | null>>, replace = false) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(changes)) {
            if (value === null || value === undefined || value === '') next.delete(key);
            else next.set(key, String(value));
          }
          return next;
        },
        { replace },
      );
    },
    [setParams],
  );

  const [input, setInput] = useState(q);
  // The last `q` this page wrote itself. Back/forward or a pasted link moves
  // `q` without a keystroke and the box must follow; our own debounce landing
  // must not, or a character typed in the gap would be thrown away.
  const ownQ = useRef(q);
  useEffect(() => {
    if (q === ownQ.current) return;
    ownQ.current = q;
    setInput(q);
  }, [q]);

  // ~300ms after the last keystroke the search goes into the URL, and with it
  // into the request path. Refining a search replaces the history entry, so
  // back does not replay every term typed; starting or clearing one pushes,
  // so back returns to the unfiltered page you were reading.
  useEffect(() => {
    const next = input.trim().slice(0, MAX_Q);
    if (next === q) return;
    const timer = window.setTimeout(() => {
      ownQ.current = next;
      navigateTo({ q: next || null, page: null }, Boolean(q) && Boolean(next));
    }, 300);
    return () => window.clearTimeout(timer);
  }, [input, q, navigateTo]);

  // URLSearchParams, not string concatenation: a `+` in an email would
  // otherwise arrive at the server as a space.
  const path = useMemo(() => {
    const search = new URLSearchParams();
    if (q) search.set('q', q);
    search.set('status', status);
    search.set('limit', String(size));
    search.set('offset', String(offset));
    return `/api/clinics?${search.toString()}`;
  }, [q, status, size, offset]);

  const clinics = useResource<ClinicListResponse>(path);

  // A page past the end — a filter shrank the set, a retire emptied the last
  // page, or a stale link — goes to the last page, but only once the answer
  // for *this* request has landed: while it is in flight, `total` is the
  // previous query's.
  useEffect(() => {
    const data = clinics.data;
    if (!data || clinics.loading || clinics.refreshing) return;
    if (data.offset !== offset || data.limit !== size) return;
    const lastPage = Math.max(1, Math.ceil(data.total / size));
    if (page > lastPage) navigateTo({ page: pageParam(lastPage) }, true);
  }, [clinics.data, clinics.loading, clinics.refreshing, offset, size, page, navigateTo]);

  const resultsRef = useRef<HTMLDivElement | null>(null);

  // Only when the table's top has scrolled away — the pager sits under it, so
  // someone paging has usually just read to the bottom. `scroll-margin-top`
  // (page-clinics.css) keeps the landing clear of the phone's sticky top bar.
  const scrollToResults = useCallback(() => {
    const element = resultsRef.current;
    if (!element) return;
    const margin = Number.parseFloat(window.getComputedStyle(element).scrollMarginTop) || 0;
    if (element.getBoundingClientRect().top >= margin) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    element.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
  }, []);

  const setStatus = (next: ClinicStatusFilter) =>
    navigateTo({ status: statusParam(next), page: null });

  const [openId, setOpenId] = useState<number | null>(null);

  const detail = useResource<ClinicDetail>(`/api/clinics/${openId ?? 0}`, {
    enabled: openId !== null,
  });
  // The resource keeps the previous clinic's data while the next one loads;
  // matching on id is what stops the panel briefly wearing the wrong record.
  const openDetail =
    openId !== null && detail.data?.doctor_id === openId ? detail.data : null;

  const rows = clinics.data?.clinics ?? [];
  const total = clinics.data?.total ?? 0;
  const counts = clinics.data?.counts ?? NO_COUNTS;

  const openRow = openId !== null ? rows.find((c) => c.doctor_id === openId) : undefined;
  const openSummary = openRow ?? openDetail ?? null;

  // Every column is unsortable: the order is the server's (doctor id), and a
  // client sort would reorder one page while claiming to order the directory.
  // DataTable still applies `initialSortKey`, which is that same order.
  const columns: ReadonlyArray<Column<ClinicSummary, string>> = useMemo(
    () => [
      {
        key: 'doctor_id',
        header: 'Doctor ID',
        sortable: false,
        render: (c) => <span className="mono">{c.doctor_id}</span>,
      },
      {
        key: 'doctor_name',
        header: 'Doctor',
        sortable: false,
        render: (c) =>
          c.doctor_name ? (
            <span className="cl-name" dir="auto">
              {c.doctor_name}
            </span>
          ) : (
            <span className="muted">{UNKNOWN}</span>
          ),
      },
      {
        key: 'doctor_email',
        header: 'Email',
        sortable: false,
        render: (c) => (
          <span className="cl-stack">
            {c.doctor_email ? (
              <span className="cl-email" dir="auto">
                {c.doctor_email}
              </span>
            ) : (
              <span className="muted">{UNKNOWN}</span>
            )}
            {loginDiffers(c) ? (
              <span
                className="cl-email cl-login"
                dir="auto"
                title="The login email (users.email) — what the doctor signs in with"
              >
                login: {c.user_email}
              </span>
            ) : null}
          </span>
        ),
      },
      {
        key: 'workspace_name',
        header: 'Workspace',
        sortable: false,
        render: (c) => (
          <span className="cl-workspace">
            {c.workspace_name ? (
              <span dir="auto">{c.workspace_name}</span>
            ) : (
              <span className="muted">{UNKNOWN}</span>
            )}
            {c.membership_count > 1 ? (
              <Tag
                title={`Member of ${c.membership_count} workspaces, ${c.active_membership_count} active — the panel lists every one`}
              >
                +{c.membership_count - 1}
              </Tag>
            ) : null}
          </span>
        ),
      },
      {
        key: 'center_slug',
        header: 'Center',
        sortable: false,
        render: (c) =>
          c.center_slug ? (
            <Link to={`/centers/${c.center_slug}`}>{c.center_slug}</Link>
          ) : (
            // No center's subdomain equals this doctor id — the clinic is
            // orphaned, and the Orphaned tile above counts exactly these.
            <span className="muted">{UNKNOWN}</span>
          ),
      },
      {
        key: 'status',
        header: 'Status',
        sortable: false,
        render: (c) => <ClinicStatusBadge clinic={c} />,
      },
      {
        key: 'edit',
        header: <span className="visually-hidden">Open</span>,
        sortable: false,
        width: '2.5rem',
        // The row click is a convenience. This is the keyboard path into the
        // panel, where every write on this page lives.
        render: (c) => (
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={ChevronRightIcon}
            iconDescription={`Open ${c.doctor_name ?? `doctor ${c.doctor_id}`}`}
            onClick={() => setOpenId(c.doctor_id)}
          />
        ),
      },
    ],
    [],
  );

  // Distinct empties, never conflated: the server finding nothing for a
  // search is not the directory being empty, and a status filter emptying the
  // view is neither. While a request is in flight the rows on screen answer
  // the previous one, and a non-zero total with no rows is a page past the
  // end about to be clamped — declaring anything empty from either would be
  // answering with stale data, so both render as loading.
  const emptyBlock =
    clinics.refreshing || total > 0 ? (
      <SkeletonRows rows={3} />
    ) : counts.all === 0 && !q ? (
      <EmptyBlock
        title="No clinics exist"
        action={
          <Link to="/centers/new" className="btn btn--primary btn--md">
            Create a center
          </Link>
        }
      >
        The shared clinical database has no doctor rows. Creating a center finds
        or registers its doctor — this page reads and amends what exists.
      </EmptyBlock>
    ) : counts.all > 0 && status === 'orphaned' ? (
      <EmptyBlock title="No orphaned clinics">
        {q ? (
          <>
            Every clinic matching <strong>{q}</strong> has a center routing to it.
          </>
        ) : (
          'Every clinic has a center routing to it.'
        )}
      </EmptyBlock>
    ) : counts.all > 0 && status === 'inactive' ? (
      <EmptyBlock title="No inactive clinics">
        {q ? (
          <>
            Every clinic matching <strong>{q}</strong> has at least one active
            workspace membership.
          </>
        ) : (
          'Every clinic has at least one active workspace membership.'
        )}
      </EmptyBlock>
    ) : (
      <EmptyBlock title="No clinics match">
        No doctor&rsquo;s name, email, login email or id contains <strong>{q}</strong>.
      </EmptyBlock>
    );

  const countText =
    status === 'all'
      ? `${formatNumber(total)} ${pluralise(total, 'clinic')}`
      : `${formatNumber(total)} of ${formatNumber(counts.all)}`;

  return (
    <>
      <PageHeader
        title="Clinics"
        status={
          clinics.data ? (
            counts.orphaned > 0 ? (
              <Badge tone="warning">{formatNumber(counts.orphaned)} orphaned</Badge>
            ) : (
              <Badge tone="good">
                {formatNumber(counts.all)} {pluralise(counts.all, 'clinic')}
              </Badge>
            )
          ) : null
        }
      />

      {clinics.loading && !clinics.data ? (
        <SkeletonRows rows={5} />
      ) : clinics.error && !clinics.data ? (
        <ErrorBlock
          what="the clinic directory"
          error={clinics.error}
          onRetry={clinics.reload}
        />
      ) : (
        <>
          {clinics.error ? (
            <StaleBanner error={clinics.error} onRetry={clinics.reload} />
          ) : null}

          <FilterBar
            search={input}
            onSearch={setInput}
            searchPlaceholder="Search by name, email, login email or doctor id"
            searchLabel="Search doctors"
            count={
              <>
                {/* Always mounted, so the live region exists before its text
                    changes — a status node inserted with its content already
                    in it is not reliably announced. */}
                <span className="cl-busy" role="status">
                  {clinics.refreshing ? (
                    <>
                      <span className="loading-inline__spinner" aria-hidden="true" />
                      Loading…
                    </>
                  ) : null}
                </span>
                {countText}
              </>
            }
          />

          <div className="cl-kpis">
            <StatGrid>
              <FilterTile
                label="Total"
                value={formatNumber(counts.all)}
                hint={q ? 'matching the search' : 'doctors in the directory'}
                active={status === 'all'}
                onPick={() => setStatus('all')}
              />
              <FilterTile
                label="Orphaned"
                value={formatNumber(counts.orphaned)}
                hint="no center routes here"
                tone={counts.orphaned > 0 ? 'warning' : undefined}
                active={status === 'orphaned'}
                onPick={(active) => setStatus(active ? 'all' : 'orphaned')}
              />
              <FilterTile
                label="Inactive"
                value={formatNumber(counts.inactive)}
                hint="no active membership"
                active={status === 'inactive'}
                onPick={(active) => setStatus(active ? 'all' : 'inactive')}
              />
            </StatGrid>
          </div>

          {/* The previous page stays on screen, dimmed, while the next one
              loads: blanking it would make every page turn flash, and
              aria-busy tells assistive tech the region is mid-update. */}
          <div
            ref={resultsRef}
            className="cl-results"
            aria-busy={clinics.refreshing || undefined}
          >
            <Card flush className="cl-table">
              <DataTable<ClinicSummary, string>
                rows={rows}
                columns={columns}
                getRowId={(c) => String(c.doctor_id)}
                label="Clinics"
                initialSortKey="doctor_id"
                onRowClick={(c) => setOpenId(c.doctor_id)}
                isRowActive={(c) => c.doctor_id === openId}
                empty={emptyBlock}
              />
              {rows.length > 0 && total > 0 ? (
                <Pager
                  page={page}
                  pageSize={size}
                  total={total}
                  onPageChange={(next) => {
                    navigateTo({ page: pageParam(next) });
                    scrollToResults();
                  }}
                  onPageSizeChange={(next) => {
                    navigateTo({ size: sizeParam(next), page: null });
                    scrollToResults();
                  }}
                />
              ) : null}
            </Card>
          </div>
        </>
      )}

      <DetailPanel
        open={openId !== null}
        title={
          openSummary?.doctor_name ??
          (openId !== null ? `Doctor ${openId}` : '')
        }
        subtitle={openSummary?.doctor_email ?? undefined}
        width="38rem"
        onClose={() => setOpenId(null)}
      >
        {openId !== null ? (
          // The error is only trusted once the request has settled: useResource
          // clears it on success alone, so while a new clinic's fetch is in
          // flight the panel would otherwise flash the previous clinic's
          // failure attributed to this one.
          detail.error && !openDetail && !detail.loading && !detail.refreshing ? (
            <ErrorBlock what="this clinic" error={detail.error} onRetry={detail.reload} />
          ) : !openDetail ? (
            <SkeletonRows rows={4} />
          ) : (
            <ClinicPanel
              key={openDetail.doctor_id}
              detail={openDetail}
              onOpenDoctor={setOpenId}
              onChanged={() => {
                detail.reload();
                clinics.reload();
              }}
            />
          )
        ) : null}
      </DetailPanel>
    </>
  );
}

/* ── the panel: record, edits, access, history ──────────────────────────── */

function ClinicPanel({
  detail,
  onOpenDoctor,
  onChanged,
}: {
  detail: ClinicDetail;
  onOpenDoctor: (doctorId: number) => void;
  onChanged: () => void;
}) {
  return (
    <>
      <section className="stack">
        <div className="row">
          <ClinicStatusBadge clinic={detail} />
          {detail.center_slug === null ? (
            <Badge
              tone="warning"
              small
              title="No center's subdomain equals this doctor id — the clinic keeps working for its users while belonging to nothing."
            >
              Orphaned
            </Badge>
          ) : null}
        </div>
        <KeyValue>
          <KeyValueRow label="Doctor id">
            <span className="mono">{detail.doctor_id}</span>
          </KeyValueRow>
          <KeyValueRow label="Center">
            {detail.center_slug ? (
              <Link to={`/centers/${detail.center_slug}`}>{detail.center_slug}</Link>
            ) : (
              <span className="muted">No center routes here</span>
            )}
          </KeyValueRow>
          <KeyValueRow label="Login user">
            {detail.user_id !== null ? (
              <span className="mono">{detail.user_id}</span>
            ) : (
              <span className="muted">No users row</span>
            )}
          </KeyValueRow>
          <KeyValueRow label="Memberships">
            {detail.membership_count > 0 ? (
              `${formatNumber(detail.active_membership_count)} of ${formatNumber(detail.membership_count)} active`
            ) : (
              <span className="muted">None</span>
            )}
          </KeyValueRow>
          <KeyValueRow label="Created">
            <span title={formatDateTime(detail.created_at)}>
              {detail.created_at ? formatRelative(detail.created_at) : UNKNOWN}
            </span>
          </KeyValueRow>
        </KeyValue>
      </section>

      <section className="stack">
        <h3 className="section__subtitle">Edit</h3>
        <ClinicEditForm
          // Reseed from the server's answer after every recorded amendment —
          // a successful PUT adds a revision, so the key moves and the fields
          // pick up what the database actually stored.
          key={`${detail.doctor_id}:${detail.revisions.length}`}
          detail={detail}
          onSaved={onChanged}
        />
      </section>

      <section className="stack">
        <h3 className="section__subtitle">
          Workspaces
          {detail.memberships.length > 0 ? (
            <span className="section__count"> {detail.memberships.length}</span>
          ) : null}
        </h3>
        <WorkspaceList detail={detail} onOpenDoctor={onOpenDoctor} onChanged={onChanged} />
      </section>

      <section className="stack">
        <h3 className="section__subtitle">
          Revisions
          {detail.revisions.length > 0 ? (
            <span className="section__count"> {detail.revisions.length}</span>
          ) : null}
        </h3>
        {detail.revisions.length === 0 ? (
          <p className="muted">
            No amendments recorded. The clinical tables keep no history of their
            own, so this list covers only what this controller changed.
          </p>
        ) : (
          <RevisionList revisions={detail.revisions} />
        )}
      </section>
    </>
  );
}

/** The membership the list row shows — the one a workspace rename targets. */
function primaryMembership(detail: ClinicDetail): ClinicMembership | null {
  return (
    detail.memberships.find((m) => m.membership_id === detail.primary_membership_id) ??
    detail.memberships.find((m) => m.is_primary) ??
    null
  );
}

/** The server's own email shape (ClinicUpdateRequest): one @, a dot after
 *  it, no whitespace. Checked here too so a bad value never reaches a 422. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Why a field's edit would be refused, or null. Only a value that would be
 * sent counts — one that differs from what is stored — so a legacy value the
 * operator did not touch never blocks the form. A field whose stored value is
 * not empty may not be cleared: users.email is what the gateway signs the
 * clinician in with, and '' there silently locks them out.
 */
function editProblem(
  current: string,
  stored: string | null,
  kind: 'email' | 'name',
  enabled = true,
): string | null {
  if (!enabled) return null;
  const value = current.trim();
  if (value === (stored ?? '')) return null;
  if (!value) {
    return kind === 'email' ? 'An email cannot be cleared' : 'A name cannot be cleared';
  }
  if (kind === 'email' && !EMAIL_SHAPE.test(value)) return 'Not a valid email address';
  return null;
}

function ClinicEditForm({
  detail,
  onSaved,
}: {
  detail: ClinicDetail;
  onSaved: () => void;
}) {
  const [doctorName, setDoctorName] = useState(detail.doctor_name ?? '');
  const [doctorEmail, setDoctorEmail] = useState(detail.doctor_email ?? '');
  const [userName, setUserName] = useState(detail.user_name ?? '');
  const [userEmail, setUserEmail] = useState(detail.user_email ?? '');
  const [workspaceName, setWorkspaceName] = useState(detail.workspace_name ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasUser = detail.user_id !== null;
  const primary = primaryMembership(detail);
  // The server refuses (409) a rename of a workspace this doctor does not
  // administer: a member-doctor's primary workspace can be another doctor's
  // clinic, and renaming it would rename that clinic.
  const canRename = primary !== null && primary.admin === 1;

  // Only what actually changed goes on the wire: the server records each
  // amended field in clinic_revisions, and a PUT restating every value would
  // write a history of edits nobody made.
  function buildBody(): ClinicUpdateRequest {
    const body: ClinicUpdateRequest = {};
    if (doctorName.trim() !== (detail.doctor_name ?? '')) body.doctor_name = doctorName.trim();
    if (doctorEmail.trim() !== (detail.doctor_email ?? '')) body.doctor_email = doctorEmail.trim();
    if (hasUser && userName.trim() !== (detail.user_name ?? '')) body.user_name = userName.trim();
    if (hasUser && userEmail.trim() !== (detail.user_email ?? '')) body.user_email = userEmail.trim();
    if (canRename && workspaceName.trim() !== (detail.workspace_name ?? '')) {
      body.workspace_name = workspaceName.trim();
    }
    return body;
  }

  const dirty = Object.keys(buildBody()).length > 0;

  // A blank or malformed email (and a cleared name) is refused before it is
  // sent: this form writes to the production clinical database. The server
  // refuses the same values with a 422; this only spares the round trip.
  const problems = {
    doctorName: editProblem(doctorName, detail.doctor_name, 'name'),
    doctorEmail: editProblem(doctorEmail, detail.doctor_email, 'email'),
    userName: editProblem(userName, detail.user_name, 'name', hasUser),
    userEmail: editProblem(userEmail, detail.user_email, 'email', hasUser),
    workspaceName: editProblem(workspaceName, detail.workspace_name, 'name', canRename),
  };
  const invalid = Object.values(problems).some((p) => p !== null);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await apiPut(`/api/clinics/${detail.doctor_id}`, buildBody());
      onSaved();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  const workspaceLabel = primary
    ? `workspace #${primary.workspace_id}${primary.workspace_name ? ` “${primary.workspace_name}”` : ''}`
    : '';

  return (
    <>
      {error ? (
        <Callout tone="critical" title="Could not save">
          {error}
        </Callout>
      ) : null}

      <div className="cl-grid">
        <TextInput
          id="cl-doctor-name"
          labelText="Doctor name"
          dir="auto"
          value={doctorName}
          invalid={problems.doctorName !== null}
          invalidText={problems.doctorName}
          onChange={(e) => setDoctorName(e.target.value)}
        />
        <TextInput
          id="cl-doctor-email"
          labelText="Doctor email"
          value={doctorEmail}
          invalid={problems.doctorEmail !== null}
          invalidText={problems.doctorEmail}
          onChange={(e) => setDoctorEmail(e.target.value)}
        />
        <TextInput
          id="cl-user-name"
          labelText="Login name"
          dir="auto"
          value={userName}
          disabled={!hasUser}
          invalid={problems.userName !== null}
          invalidText={problems.userName}
          helperText={hasUser ? undefined : 'No users row exists — this doctor has no Health-360 login.'}
          onChange={(e) => setUserName(e.target.value)}
        />
        <TextInput
          id="cl-user-email"
          labelText="Login email"
          value={userEmail}
          disabled={!hasUser}
          invalid={problems.userEmail !== null}
          invalidText={problems.userEmail}
          helperText={hasUser ? 'What the clinician signs in with.' : undefined}
          onChange={(e) => setUserEmail(e.target.value)}
        />
        <TextInput
          id="cl-workspace-name"
          labelText={primary ? `Workspace name (#${primary.workspace_id})` : 'Workspace name'}
          dir="auto"
          value={workspaceName}
          disabled={!canRename}
          invalid={problems.workspaceName !== null}
          invalidText={problems.workspaceName}
          helperText={
            primary === null
              ? 'This doctor belongs to no workspace, so there is nothing to rename.'
              : canRename
                ? `Renames ${workspaceLabel}, the primary membership, which this doctor administers.`
                : `Locked: this doctor is a member of ${workspaceLabel}, not its admin, so a new name would rename another doctor’s clinic.`
          }
          onChange={(e) => setWorkspaceName(e.target.value)}
        />
      </div>

      <div className="row">
        <Button onClick={save} disabled={busy || !dirty || invalid}>
          {busy ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
      <p className="field__help muted">
        Writes to the shared clinical database immediately — owner only, and
        every change is recorded as a revision below. The doctor id itself is
        never editable: it is what routes a center here.
      </p>
    </>
  );
}

/* ── workspaces: one grant per membership ────────────────────────────────── */

function WorkspaceList({
  detail,
  onOpenDoctor,
  onChanged,
}: {
  detail: ClinicDetail;
  onOpenDoctor: (doctorId: number) => void;
  onChanged: () => void;
}) {
  const shared = detail.shared_with_doctor_ids;
  return (
    <>
      {shared.length > 0 ? (
        <Callout tone="warning" title="This login is shared with other doctors">
          <p>
            {pluralise(shared.length, 'Doctor', 'Doctors')}{' '}
            {shared.map((id, index) => (
              <span key={id}>
                {index > 0 ? ', ' : null}
                <Button
                  kind="ghost"
                  size="sm"
                  className="cl-doctor-link"
                  aria-label={`Open doctor ${id}`}
                  onClick={() => onOpenDoctor(id)}
                >
                  {id}
                </Button>
              </span>
            ))}{' '}
            {shared.length === 1 ? 'signs' : 'sign'} in with the same users row.
            Memberships belong to the login, not to one doctor, so retiring or
            restoring one here changes their access too.
          </p>
        </Callout>
      ) : null}

      {detail.memberships.length === 0 ? (
        <p className="muted">
          {detail.user_id === null
            ? 'No users row exists, so there are no memberships — nothing to retire or restore.'
            : 'This login belongs to no workspace — there is nothing to retire or restore.'}
        </p>
      ) : (
        <ul className="cl-members">
          {detail.memberships.map((membership) => (
            <MembershipItem
              key={membership.membership_id}
              doctorId={detail.doctor_id}
              membership={membership}
              onChanged={onChanged}
            />
          ))}
        </ul>
      )}

      <p className="field__help muted">
        Retiring sets one membership&rsquo;s <code>workspaces_users.active</code> to
        0, so the WebAPI stops offering that workspace to this login. It is not a
        lockout: the doctor&rsquo;s own PACS still resolves by doctor id, and an
        invite to the workspace can re-activate the membership. Nothing is
        deleted, so restoring is one action.
      </p>
    </>
  );
}

type MembershipState = 'active' | 'retired' | 'pending' | 'unflagged';

/**
 * `active = 0` means two things in the WebAPI — revoked, and invited but not
 * yet accepted — so the flag alone cannot say which. This controller's own
 * revision history can: a 0 that the last recorded action here set is ours.
 */
function membershipState(membership: ClinicMembership): MembershipState {
  if (membership.active === null) return 'unflagged';
  if (membership.active !== 0) return 'active';
  return membership.last_fleet_action === 'retire' ? 'retired' : 'pending';
}

const MEMBERSHIP_BADGE: Record<MembershipState, { tone: Tone; label: string; title: string }> = {
  active: {
    tone: 'good',
    label: 'Active',
    title: 'workspaces_users.active is 1 — the WebAPI offers this workspace to the login.',
  },
  retired: {
    tone: 'neutral',
    label: 'Retired here',
    title: 'Set to 0 by a retirement recorded in this panel. The rows are kept.',
  },
  pending: {
    tone: 'info',
    label: 'Pending',
    title:
      'active is 0, but this panel never retired it. The WebAPI stores an invite nobody accepted the same way, so this is most likely a pending invite — or it was deactivated elsewhere.',
  },
  unflagged: {
    tone: 'warning',
    label: 'No flag',
    title: 'workspaces_users.active is NULL — the row says neither granted nor revoked.',
  },
};

function MembershipItem({
  doctorId,
  membership,
  onChanged,
}: {
  doctorId: number;
  membership: ClinicMembership;
  onChanged: () => void;
}) {
  const [retiring, setRetiring] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = membershipState(membership);
  const badge = MEMBERSHIP_BADGE[state];
  const pin = membership.pinned_doctor_id;
  const formId = `cl-retire-${membership.membership_id}`;

  // `membership_id` always goes on the wire, even for a doctor with one
  // membership: the server requires it once there are several, and naming
  // the row explicitly means a membership added meanwhile cannot be hit
  // instead.
  async function post(action: 'retire' | 'restore', body: ClinicRetireRequest | ClinicRestoreRequest) {
    setBusy(true);
    setError(null);
    try {
      await apiPost(`/api/clinics/${doctorId}/${action}`, body);
      setReason('');
      setRetiring(false);
      onChanged();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="cl-member">
      <div className="row">
        {membership.workspace_name ? (
          <span className="cl-member__name" dir="auto">
            {membership.workspace_name}
          </span>
        ) : (
          <span className="muted">Unnamed workspace</span>
        )}
        <span className="mono muted">#{membership.workspace_id}</span>
        <span className="push">
          <Badge
            tone={badge.tone}
            small
            title={
              state === 'retired' && membership.last_fleet_action_at
                ? `${badge.title} Retired ${formatDateTime(membership.last_fleet_action_at)}.`
                : badge.title
            }
          >
            {badge.label}
          </Badge>
        </span>
      </div>

      {membership.is_primary || membership.admin === 1 || membership.default_workspace === 1 ? (
        <div className="row">
          {membership.is_primary ? (
            <Tag title="The membership the directory list shows for this doctor, and the workspace the Edit form renames. A display choice only — the PACS has no primary workspace.">
              Primary
            </Tag>
          ) : null}
          {membership.admin === 1 ? (
            <Tag title="This login administers the workspace (workspaces_users.admin = 1).">
              Admin
            </Tag>
          ) : null}
          {membership.default_workspace === 1 ? (
            <Tag title="The workspace this login opens into (default_workspace = 1).">
              Default
            </Tag>
          ) : null}
        </div>
      ) : null}

      {pin !== null ? (
        <p className={`cl-member__pin${pin !== doctorId ? ' cl-member__pin--other' : ''}`}>
          PACS pin: routes to doctor <span className="mono">{pin}</span>
          {pin === doctorId
            ? ' — this doctor.'
            : ' — a different doctor, so this workspace’s studies come from that doctor’s PACS, not this one’s.'}
        </p>
      ) : null}

      {error ? (
        <Callout tone="critical" title={state === 'active' ? 'Could not retire' : 'Could not restore'}>
          {error}
        </Callout>
      ) : null}

      {state === 'active' ? (
        <>
          {/* One button that opens and cancels, rather than a Cancel inside
              the form: it stays mounted either way, so keyboard focus is
              never dropped out of the panel's focus trap onto <body>. */}
          <div className="row">
            <Button
              kind={retiring ? 'ghost' : 'tertiary'}
              size="sm"
              aria-expanded={retiring}
              aria-controls={retiring ? formId : undefined}
              disabled={busy}
              onClick={() => {
                setRetiring(!retiring);
                setReason('');
                setError(null);
              }}
            >
              {retiring ? 'Cancel retirement' : 'Retire…'}
            </Button>
          </div>
          {retiring ? (
            <div id={formId} className="stack cl-member__retire">
              <TextArea
                id={`${formId}-reason`}
                labelText="Reason"
                rows={2}
                value={reason}
                autoFocus
                helperText="Required. Retiring destroys nothing, so the reason is the only part nobody can reconstruct later."
                onChange={(e) => setReason(e.target.value)}
              />
              <div className="row">
                <Button
                  kind="danger"
                  size="sm"
                  disabled={busy || !reason.trim()}
                  onClick={() =>
                    post('retire', {
                      reason: reason.trim(),
                      membership_id: membership.membership_id,
                    })
                  }
                >
                  {busy ? 'Retiring…' : 'Retire membership'}
                </Button>
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <div className="row">
          <Button
            kind="tertiary"
            size="sm"
            disabled={busy}
            onClick={() => post('restore', { membership_id: membership.membership_id })}
          >
            {busy ? 'Restoring…' : 'Restore'}
          </Button>
          {state === 'pending' ? (
            <span className="field__help">
              Sets active to 1 — for a pending invite, that grants access
              without the doctor accepting it.
            </span>
          ) : null}
        </div>
      )}
    </li>
  );
}

/* ── revisions ──────────────────────────────────────────────────────────── */

const REVISION_TONE: Record<string, Tone> = {
  update: 'info',
  retire: 'warning',
  restore: 'good',
};

/** null, undefined and "" all render as the em dash — a cleared value and an
 *  absent one look the same to the operator, and neither is a fake zero. */
function revisionValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return UNKNOWN;
  return String(value);
}

function RevisionList({ revisions }: { revisions: ClinicRevisionResponse[] }) {
  return (
    <ul className="cl-revs">
      {revisions.map((rev) => {
        // The server snapshots every editable field per revision, changed or
        // not; a diff that lists "X → X" four times to show one email edit
        // buries the edit, so only fields whose rendered value moved appear.
        const keys = Array.from(
          new Set([...Object.keys(rev.before), ...Object.keys(rev.after)]),
        ).filter((key) => revisionValue(rev.before[key]) !== revisionValue(rev.after[key]));
        return (
          <li key={rev.id} className="cl-rev">
            <div className="row">
              <Badge tone={REVISION_TONE[rev.kind] ?? 'neutral'} small>
                {rev.kind}
              </Badge>
              <span className="mono cl-rev__table">{rev.target_table}</span>
              <span className="cl-rev__actor">{rev.actor_email}</span>
              <span className="muted cl-rev__time" title={formatDateTime(rev.created_at)}>
                {formatRelative(rev.created_at)}
              </span>
            </div>
            {keys.length > 0 ? (
              <dl className="cl-rev__diff">
                {keys.map((key) => (
                  <div key={key} className="cl-rev__change">
                    <dt className="mono">{key}</dt>
                    <dd>
                      <span className="cl-rev__before">{revisionValue(rev.before[key])}</span>
                      <span aria-hidden="true"> → </span>
                      <span className="visually-hidden"> changed to </span>
                      <span className="cl-rev__after">{revisionValue(rev.after[key])}</span>
                    </dd>
                  </div>
                ))}
              </dl>
            ) : null}
            {rev.reason ? <p className="cl-rev__reason">&ldquo;{rev.reason}&rdquo;</p> : null}
          </li>
        );
      })}
    </ul>
  );
}
